import { PortalHost } from "@gorhom/portal";
import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { Platform, ScrollView, View } from "react-native";
import { KeyboardAwareScrollView as KeyboardAwareScrollViewController } from "react-native-keyboard-controller";
import { generatePath, useNavigate } from "react-router";
import { Box, Layout as AuroraLayout, Spinner } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { CommandStatus } from "@lookiero/messaging-react";
import { Country } from "@lookiero/sty-psp-locale";
import { useLogger } from "@lookiero/sty-psp-logging";
import { Segment } from "@lookiero/sty-psp-segment";
import { Button, Layout as UiLayout, Text, useScreenSize } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { CheckoutProjection } from "../../../../../../projection/checkout/checkout";
import { CheckoutItemProjection } from "../../../../../../projection/checkoutItem/checkoutItem";
import { ReturnQuestionType } from "../../../../../../projection/returnQuestion/returnQuestion.constants";
import { useReturnCheckoutItem } from "../../../../../domain/checkoutItem/react/useReturnCheckoutItem";
import { useListReturnQuestionsByCheckoutItemId } from "../../../../../projection/returnQuestion/react/useListReturnQuestionsByCheckoutItemId";
import { TrackingPage } from "../../../../../tracking/tracking";
import { useTrackPageView } from "../../../../../tracking/useTrackPageView";
import { useTrackPressBack } from "../../../../../tracking/useTrackPressBack";
import { useTrackPressNext } from "../../../../../tracking/useTrackPressNext";
import { useTrackReturnItem } from "../../../../../tracking/useTrackReturnItem";
import { ReturnQuestions } from "../../../../components/organisms/returnQuestions/ReturnQuestions";
import { useReturnQuestionFeedback } from "../../../../components/organisms/returnQuestions/behaviors/useReturnQuestionFeedback";
import {
  ReturnQuestionItemProvider,
  ReturnQuestionItems,
} from "../../../../components/organisms/returnQuestions/behaviors/useReturnQuestionItem";
import { HostDefaultReturnQuestionItem } from "../../../../components/organisms/returnQuestions/components/hostDefaultReturnQuestionItem/HostDefaultReturnQuestionItem";
import { HostLegacyRadioGroupReturnQuestionItem } from "../../../../components/organisms/returnQuestions/components/hostLegacyRadioGroupReturnQuestionItem/HostLegacyRadioGroupReturnQuestionItem";
import { HostRadioGroupReturnQuestionItem } from "../../../../components/organisms/returnQuestions/components/hostRadioGroupReturnQuestionItem/HostRadioGroupReturnQuestionItem";
import { HostStackReturnQuestionItem } from "../../../../components/organisms/returnQuestions/components/hostStackReturnQuestionItem/form/HostStackReturnQuestionItem";
import { RadioReturnQuestionItem } from "../../../../components/organisms/returnQuestions/components/radioReturnQuestionItem/RadioReturnQuestionItem";
import { TextareaReturnQuestionItem } from "../../../../components/organisms/returnQuestions/components/textareaReturnQuestionItem/TextareaReturnQuestionItem";
import { ItemDetailHeader } from "../../../../components/templates/header/itemDetailHeader/ItemDetailHeader";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Routes } from "../../../../routing/routes";
import { ProductVariantPreview } from "../productVariantPreview/ProductVariantPreview";
import { style as returnQuestionsFormStyle } from "./ReturnQuestionsForm.style";

const RETURN_QUESTION_FORM_PORTAL_HOST_NAME = "return-question-form-portal";

const returnQuestionItems: ReturnQuestionItems = {
  [ReturnQuestionType.HOST_DEFAULT]: HostDefaultReturnQuestionItem,
  [ReturnQuestionType.HOST_TEXTAREA]: HostDefaultReturnQuestionItem,
  [ReturnQuestionType.HOST_SELECT]: HostLegacyRadioGroupReturnQuestionItem,
  [ReturnQuestionType.HOST_RADIO_GROUP]: HostRadioGroupReturnQuestionItem,
  [ReturnQuestionType.HOST_STACK]: HostStackReturnQuestionItem,
  [ReturnQuestionType.TEXTAREA]: TextareaReturnQuestionItem,
  [ReturnQuestionType.OPTION]: RadioReturnQuestionItem,
};

interface ReturnQuestionsFormProps {
  readonly checkout: CheckoutProjection;
  readonly checkoutItem: CheckoutItemProjection;
  readonly country: Country;
  readonly layout: UiLayout;
  readonly onClearDraft?: () => void;
  readonly segment: Segment;
}

const ReturnQuestionsForm: FC<ReturnQuestionsFormProps> = ({
  checkout,
  checkoutItem,
  country,
  layout: Layout,
  onClearDraft,
  segment,
}) => {
  const navigate = useNavigate();
  const { basePath, tradename } = useStaticInfo();
  const logger = useLogger();
  const screenSize = useScreenSize();
  const isDektopScreen = screenSize === "L";
  const isMobileScreen = screenSize === "S";

  const style = useMemo(() => returnQuestionsFormStyle(), []);

  // react-native-keyboard-controller is native-only: on web its bindings are NOOP (no keyboard
  // events fire, the browser handles focused-input scrolling itself), so KeyboardAwareScrollView
  // adds nothing there AND its async mount effect throws under the Expo SDK 55 hermes web
  // transform ("PromiseResolve called on non-object"). Plain ScrollView on web = same behavior,
  // no crash.
  const KeyboardAwareScrollView = useMemo(
    () => (Platform.OS === "web" ? ScrollView : KeyboardAwareScrollViewController),
    [],
  );

  const titleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.RETURN_QUESTIONS_TITLE });
  const descriptionText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.RETURN_QUESTIONS_DESCRIPTION });
  const submitButtonText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.RETURN_QUESTIONS_SUBMIT_BUTTON });

  const feedback = useReturnQuestionFeedback();
  const [returnQuestions] = useListReturnQuestionsByCheckoutItemId({
    checkoutItemId: checkoutItem.id,
  });

  const [returnCheckoutItem, returnCheckoutItemStatus] = useReturnCheckoutItem({
    checkoutItemId: checkoutItem.id,
    logger,
  });
  const trackReturnItem = useTrackReturnItem({
    page: TrackingPage.RETURN,
    country,
    segment,
    tradename,
    checkoutId: checkout.id,
    checkoutItemId: checkoutItem.id,
  });

  useTrackPageView({ checkoutId: checkout.id, country, segment, tradename, page: TrackingPage.RETURN });

  const trackPressBack = useTrackPressBack({
    page: TrackingPage.ITEM,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });
  const handleOnBack = useCallback(() => {
    trackPressBack();
    navigate(-1);
  }, [navigate, trackPressBack]);

  const trackPressNext = useTrackPressNext({
    page: TrackingPage.ITEM,
    country,
    segment,
    tradename,
    checkoutId: checkout.id,
  });

  // CheckoutMiddleware reacts to the same return event and may navigate away on its own (it refetches
  // and redirects to the next undecided item) before this awaited call below resolves. Guard the
  // navigation below with this so a late-resolving handleOnSubmit never re-navigates past wherever the
  // user already ended up, which would yank them back to this item mid-interaction with the next one.
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Effects can re-run on the same instance without a prior unmount (React 18 StrictMode's
    // mount->cleanup->mount dev check, Fast Refresh): the cleanup below flips this to false, so the
    // body must flip it back to true every time the effect runs, not just rely on the useRef initializer.
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleOnSubmit = useCallback(async () => {
    const itemIndex = checkout.items.indexOf(checkoutItem);
    const nextItemIndex = itemIndex === checkout.items.length - 1 ? 0 : itemIndex + 1;
    const nextItem = checkout.items[nextItemIndex] as CheckoutItemProjection;

    // press_next reports the intent behind the press, so it is emitted at press time, before the
    // outcome is known: emitting it after the await would lose the event whenever the user leaves
    // the page during the request. `to` is therefore the item the press aimed at, not where the
    // user ends up - a failed return keeps them here and this event still names nextItem.
    // return_item reports a return that actually happened, so it stays behind the guard below.
    trackPressNext({ from: checkoutItem.id, to: nextItem.id });

    // Await the result before navigating: a failed return must leave the user on this item so they
    // can retry, instead of advancing as if the return had been saved.
    const succeeded = await returnCheckoutItem({ feedbacks: feedback });

    if (!succeeded) {
      return;
    }

    onClearDraft?.();
    trackReturnItem();

    if (!isMountedRef.current) {
      return;
    }

    navigate(generatePath(`${basePath}/${Routes.ITEM}`, { id: nextItem.id }));
  }, [
    basePath,
    checkout.items,
    checkoutItem,
    feedback,
    navigate,
    onClearDraft,
    returnCheckoutItem,
    trackPressNext,
    trackReturnItem,
  ]);

  const header = useMemo(
    () => (
      <Box style={style.headerWrapper}>
        <ItemDetailHeader onBack={handleOnBack} />
      </Box>
    ),
    [handleOnBack, style.headerWrapper],
  );

  const dependenciesLoaded = checkoutItem && returnQuestions && returnCheckoutItemStatus !== CommandStatus.LOADING;

  if (!dependenciesLoaded) {
    return <Spinner />;
  }

  return (
    <ReturnQuestionItemProvider returnQuestionItems={returnQuestionItems}>
      <PortalHost name={RETURN_QUESTION_FORM_PORTAL_HOST_NAME} />

      <Layout
        footer={null}
        header={header}
        scrollEnabled={false}
        style={{
          safeAreaView: style.safeAreaView,
          scrollView: style.scrollView,
          header: style.header,
        }}
      >
        <ProductVariantPreview country={country} item={checkoutItem} />

        <KeyboardAwareScrollView
          {...(Platform.OS !== "web" ? { bottomOffset: Platform.OS === "android" ? 60 : 120 } : {})}
          keyboardShouldPersistTaps="never"
          showsVerticalScrollIndicator={false}
          testID="return-questions-form"
        >
          <View style={style.background} testID="return-questions-background">
            <AuroraLayout
              fullWidth={!isDektopScreen}
              style={[style.layout, isDektopScreen ? style.desktopLayoutSpacing : undefined]}
            >
              <Box size={{ L: "2/3" }}>
                <View
                  style={[
                    style.info,
                    !isMobileScreen && style.infoDesktop,
                    tradename === Tradename.OUTFITTERY && style.infoOutfittery,
                  ]}
                >
                  <Text level={3} variant="heading">
                    {titleText}
                  </Text>
                  <Text level={1} style={style.description} variant="detail">
                    {descriptionText}
                  </Text>
                </View>

                <ReturnQuestions
                  portalHostName={RETURN_QUESTION_FORM_PORTAL_HOST_NAME}
                  returnQuestions={returnQuestions}
                />

                <View style={[style.submit, !isMobileScreen && style.submitDesktop]}>
                  <Button testID="return-questions-button" onPress={handleOnSubmit}>
                    {submitButtonText}
                  </Button>
                </View>
              </Box>
            </AuroraLayout>
          </View>
        </KeyboardAwareScrollView>
      </Layout>
    </ReturnQuestionItemProvider>
  );
};

export { ReturnQuestionsForm };
